/* global axios */
import ApiClient from './ApiClient';

// DEBT customization — cliente compartilhado pelo widget de funil/etapa no
// painel da conversa (T3.3) e pela página do board (T3.4). Consome a API do
// Kanban de conversas (T3.2, controllers sob Api::V2::Accounts::Debt::Kanban::*).
// conversationDisplayId é o id que o frontend já tem em mãos (json.id da
// conversa é o display_id — ver _conversation.json.jbuilder:29), não a PK.
class DebtKanbanApi extends ApiClient {
  constructor() {
    super('debt/kanban', { accountScoped: true, apiVersion: 'v2' });
  }

  // include_archived=true é a ÚNICA forma de ver funil arquivado
  // (funnels_controller.rb#index aplica o scope :active por padrão): o board e
  // o widget da conversa nunca o pedem — quem precisa dele é a tela de
  // administração, que tem de poder desarquivar.
  funnels({ includeArchived = false } = {}) {
    return axios.get(`${this.url}/funnels`, {
      params: { include_archived: includeArchived },
    });
  }

  // T3.5 — escrita de funil e etapa. TODA escrita abaixo devolve o FUNIL
  // INTEIRO (funnels/show e stages/show renderizam a mesma partial _funnel),
  // e não a entidade tocada: criar, renomear, reordenar e remover mudam a
  // lista que a tela desenha. Quem consome substitui o objeto inteiro em vez
  // de remontar a lista localmente.
  createFunnel({ name, slug = null }) {
    return axios.post(`${this.url}/funnels`, slug ? { name, slug } : { name });
  }

  // Só o nome: o slug é identidade (DebtKanbanSluggable) e o controller nem o
  // aceita aqui.
  updateFunnel(funnelId, { name }) {
    return axios.patch(`${this.url}/funnels/${funnelId}`, { name });
  }

  deleteFunnel(funnelId) {
    return axios.delete(`${this.url}/funnels/${funnelId}`);
  }

  archiveFunnel(funnelId) {
    return axios.post(`${this.url}/funnels/${funnelId}/archive`);
  }

  unarchiveFunnel(funnelId) {
    return axios.post(`${this.url}/funnels/${funnelId}/unarchive`);
  }

  createStage(funnelId, attributes) {
    return axios.post(`${this.url}/funnels/${funnelId}/stages`, attributes);
  }

  // Campo AUSENTE do corpo não é alterado; campo presente com null é limpo
  // (stages_controller.rb#stage_policy_attributes). Por isso `attributes` é
  // repassado como veio: montar aqui um objeto com os quatro campos sempre
  // presentes apagaria em silêncio o que o formulário não editou.
  updateStage(funnelId, stageId, attributes) {
    return axios.patch(
      `${this.url}/funnels/${funnelId}/stages/${stageId}`,
      attributes
    );
  }

  // target_stage_id viaja no CORPO do DELETE (axios `data`), que é como o
  // controller o lê (params[:target_stage_id]). Ausente, a etapa com card
  // responde 409 e o destino é perguntado ao usuário.
  deleteStage(funnelId, stageId, { targetStageId = null } = {}) {
    return axios.delete(`${this.url}/funnels/${funnelId}/stages/${stageId}`, {
      data: targetStageId ? { target_stage_id: targetStageId } : {},
    });
  }

  // A lista COMPLETA das etapas do funil, na ordem desejada — o controller
  // recusa lista parcial ou com id de fora com 422.
  reorderStages(funnelId, stageIds) {
    return axios.patch(`${this.url}/funnels/${funnelId}/stages/reorder`, {
      stage_ids: stageIds,
    });
  }

  card(conversationDisplayId) {
    return axios.get(`${this.url}/conversations/${conversationDisplayId}/card`);
  }

  // funnelId null mantém o card no funil corrente (comportamento do
  // controller quando o parâmetro está ausente/em branco).
  moveCard(cardId, { stageId, funnelId = null, lockVersion }) {
    return axios.patch(`${this.url}/cards/${cardId}`, {
      stage_id: stageId,
      funnel_id: funnelId,
      lock_version: lockVersion,
    });
  }

  // T3.4 página do board. params é repassado como está (funnel_id,
  // last_activity_after, assignee_id[], inbox_id[], debt_state[], label[],
  // priority[], status[], q, order) — board_controller.rb já valida/rejeita
  // o que não reconhece, então não há necessidade de espelhar essa lista
  // aqui e arriscar divergir dela.
  board(params) {
    return axios.get(`${this.url}/board`, { params });
  }

  // Paginação por coluna (scroll infinito). stageId é obrigatório no
  // controller (board_controller.rb#column); os demais params seguem o
  // mesmo contrato de board().
  //
  // `cursor` é opaco: é o next_cursor devolvido pela página anterior, e o
  // cliente nunca o interpreta. `page` (OFFSET) não existe mais nesta rota —
  // o controller responde 422 se ele vier, em vez de devolver a primeira
  // página em silêncio.
  boardColumn({ stageId, cursor = null, ...params }) {
    return axios.get(`${this.url}/board/column`, {
      params: { ...params, stage_id: stageId, cursor },
    });
  }
}

export default new DebtKanbanApi();
